'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Box } from '@kohler-dsm-library/kohler-core';
import { ArrowRightIcon, ArrowLeftIcon } from '@kohler-dsm-library/kohler-icons';
import Thumbnails from './Thumbnails';
import { buildDsmImageProps } from '@/aem-dsm-mappers/utils/imageProps';
import styles from '../productFeatureCarousel.module.css';

export default function ImageCarousel({ images = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  if (!images.length) return null;

  const safeIndex = activeIndex % images.length;

  // get exact slide width instead of container width
  const getSlideWidth = () => {
    const el = scrollRef.current;
    if (!el || !el.firstChild) return 0;
    return el.firstChild.clientWidth;
  };

  const scrollToIndex = (index) => {
    const el = scrollRef.current;
    if (!el) return;

    const slideWidth = getSlideWidth();

    el.scrollTo({
      left: slideWidth * index,
      behavior: 'auto',
    });
  };

  const goNext = () => {
    const next = (safeIndex + 1) % images.length;
    setActiveIndex(next);
    scrollToIndex(next);
  };

  const goPrev = () => {
    const prev = safeIndex === 0 ? images.length - 1 : safeIndex - 1;
    setActiveIndex(prev);
    scrollToIndex(prev);
  };

  const handleThumbnailClick = useCallback((index) => {
    setActiveIndex(index);
    scrollToIndex(index);
  }, []);

  // use slide width here also
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    const slideWidth = getSlideWidth();
    if (!slideWidth) return;

    const index = Math.round(el.scrollLeft / slideWidth);

    if (index !== safeIndex) {
      setActiveIndex(index);
    }
  };

  return (
    <>
      <Box className={styles.carouselRoot}>
        {/* ===== MAIN SCROLL AREA ===== */}
        <Box className={styles.carouselScroll} ref={scrollRef} onScroll={handleScroll}>
          {images.map((img, index) => (
            <Box className={styles.carouselItem} key={img.image || index}>
              <img
                {...buildDsmImageProps({
                  src: images[safeIndex % images.length].image,
                  alt: images[safeIndex % images.length].imageAltText || '',
                  allowEmptySrc: true,
                })}
              />
            </Box>
          ))}
        </Box>

        {/* ===== PREVIEW IMAGE ===== */}
        <Box className={styles.previewImage}>
          <img
            {...buildDsmImageProps({
              src: images[(safeIndex + 1) % images.length].image,
              alt: images[(safeIndex + 1) % images.length].imageAltText || '',
              allowEmptySrc: true,
            })}
          />
        </Box>

        {/* ===== BUTTONS ===== */}
        <button className={styles.prevBtn} onClick={goPrev}>
          <ArrowLeftIcon />
        </button>

        <button className={styles.nextBtn} onClick={goNext}>
          <ArrowRightIcon />
        </button>
      </Box>

      {/* ===== THUMBNAILS ===== */}
      <Thumbnails images={images} activeIndex={safeIndex} onSelect={handleThumbnailClick} />
    </>
  );
}
